import { Alert, DrilldownCell, SimpleTable, SeverityPill, TableSkeleton } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import {
  ApiService,
  type InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatDate, standardDateTimeFormat } from '@/utils/formatDate'

export interface AlertAcknowledgementDetailParams extends InsightParametersType {
  companyId?: number
  alertConfigIds?: string
  status?: string
  severity?: string[]
  date?: string
  from?: string
  to?: string
}

interface ApiRow {
  id: string
  asset: { id: number; name: string }
  alertConfigId: string
  alertName: string
  monitoringPoint: string | null
  severity: number
  raisedTime: string
  raisedLocation: string | null
  raisedLatitude: number | null
  raisedLongitude: number | null
  clearedTime: string | null
  acknowledged: boolean
  actionCount: number
}

interface AlertAcknowledgementDetailRow {
  id: string
  asset: { id: number; name: string }
  alertConfigId: string
  alertName: string
  monitoringPoint: string | null
  severity: number
  raisedTime: string
  raisedLocation: string | null
  raisedLatitude: number | null
  raisedLongitude: number | null
  clearedTime: string | null
  acknowledged: boolean
  actionCount: number
}

const statusOptions = [
  { label: 'All', value: '', isDefault: true },
  { label: 'Raised', value: 'raised' },
  { label: 'Cleared', value: 'cleared' },
]

const AlertAcknowledgementDetail: InsightDefinition<AlertAcknowledgementDetailParams, AlertAcknowledgementDetailRow[]> =
  {
    active: true,
    slug: 'alert/acknowledgement-detail',
    category: 'alert',
    name: 'Alert Acknowledgement Detail',
    description: 'List alerts with acknowledgement status and action count for companies with acknowledgement enabled',
    defaultWidth: 'full',
    defaultAspectRatio: 16 / 9,

    configuration: {
      transform: ({ parameters, field }) => {
        if (field === 'severity') return { ...parameters, alertConfigIds: undefined }
        if (field === 'alertConfigIds') return { ...parameters, severity: undefined }
        if (field === 'date' && parameters.date !== 'custom') {
          return { ...parameters, from: undefined, to: undefined }
        }
        return parameters
      },

      controls: [
        { id: 'companyId', control: 'CompanyDropdown', required: true, acknowledgementOnly: true },
        {
          id: 'status',
          control: 'CustomSingleSelect',
          label: 'Alert Status',
          options: statusOptions,
        },
        { id: 'note', control: 'Label', label: 'Choose either Severity or Alert Name' },
        { id: 'alertConfigIds', control: 'AlertNameDropdown', required: false, acknowledgementOnly: true },
        { id: 'severity', control: 'SeverityDropdown' },
        { id: 'date', control: 'CustomSingleSelect', options: DateOptions },
        {
          id: 'customDate',
          label: 'Raised Date',
          control: 'DateTimeRange',
          dependsOn: 'date',
          dependsOnValue: 'custom',
        },
      ],

      isValid: (configuration: AlertAcknowledgementDetailParams) => {
        if (!configuration.companyId) {
          return 'Company is required.'
        }

        if (configuration.isDrilldown) {
          return true
        }
        const hasSeverity = configuration.severity && configuration.severity.length > 0
        const hasAlert = configuration.alertConfigIds && configuration.alertConfigIds.length > 0

        if (hasSeverity && hasAlert) {
          return 'Choose either Alert Name or Severity'
        }
        if (!hasSeverity && !hasAlert) {
          return 'Alert Name or Severity is required'
        }

        if (configuration.date === 'custom') {
          if (!configuration.from && !configuration.to) {
            return 'Enter the custom date for Raised Date'
          }
          if (configuration.from && configuration.to) {
            if (new Date(configuration.from) > new Date(configuration.to)) {
              return 'Start date must be before end date'
            }
          }
          const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
          if (dateValidation !== true) {
            return dateValidation
          }
        }

        return true
      },
    },

    csv: async (api: ApiService, parameters: AlertAcknowledgementDetailParams, title: string) => {
      await downloadCsv(api, '/insights/alerts/alert-acknowledgement/details', parameters, title)
    },

    load: async (_api: ApiService, _parameters: AlertAcknowledgementDetailParams) => {
      const path = `/insights/alerts/alert-acknowledgement/details?${buildQuery(_parameters)}&skip=0&take=1000`
      const response = await _api.get<ApiRow[]>({ path, origin: 'AlertAcknowledgementDetail' })
      if (response.error) return { code: InsightStatusCode.ERROR, message: response.message }
      if (!response.data?.length) return { code: InsightStatusCode.NO_DATA }
      return response.data
    },

    Display: ({ status, data, configuration }) => {
      const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })

      if (status.code === InsightStatusCode.NOT_CONFIGURED) {
        return <Alert type="info" message={status.message || 'Insight has not been configured'} />
      }
      if (showSkeleton) {
        return <TableSkeleton columns={9} withHeader rowHeight={35} />
      }
      if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
        return null
      }
      if (isInsightStatus(data)) {
        return null
      }
      if (!data?.length) {
        return null
      }

      const parameters = configuration.parameters as AlertAcknowledgementDetailParams

      const body = data.map((row, index) => {
        const drillConfig: InsightConfigurationType = {
          ...configuration,
          slug: 'asset/alert-action-history',
          title: `Alert Acknowledgement By Asset: ${row.asset.name}`,
          parameters: {
            companyId: parameters.companyId,
            assetId: String(row.asset.id),
            alertIds: row.id,
            severity: [String(row.severity)],
            date: parameters.date,
            from: new Date(row.raisedTime).toISOString(),
            to: parameters.to,
            isDrilldown: true,
          },
        }

        const acknowledgedCell: DrilldownCell | string = row.acknowledged
          ? {
              value: 'Yes',
              configuration: drillConfig,
              id: `ack-${row.id}-${index}`,
            }
          : 'No'

        const actionCountCell: DrilldownCell | string =
          row.actionCount > 0
            ? {
                value: String(row.actionCount),
                configuration: drillConfig,
                id: `action-count-${row.id}-${index}`,
              }
            : String(row.actionCount)

        return {
          'Asset Name': row.asset.name,
          'Alert Name': row.alertName,
          'Monitoring Point': row.monitoringPoint ?? '-',
          Severity: {
            value: row.severity,
            display: <SeverityPill label={getSeverityLabel(String(row.severity))} severityCode={row.severity} />,
          },
          'Raised Date': {
            id: `raised-${row.id}-${index}`,
            date: row.raisedTime,
            display: formatDate(new Date(row.raisedTime), standardDateTimeFormat),
          },
          'Raised Location': genericLocationDrilldown({
            configuration,
            address: row.raisedLocation ?? undefined,
            latitude: row.raisedLatitude ?? undefined,
            longitude: row.raisedLongitude ?? undefined,
            companyId: parameters.companyId,
            markerFields: {
              Asset: row.asset.name,
              Alert: row.alertName,
              Time: row.raisedTime.toLocaleString(),
            },
          }),
          'Cleared Date': row.clearedTime
            ? {
                id: `cleared-${row.id}-${index}`,
                date: row.clearedTime,
                display: formatDate(new Date(row.clearedTime), standardDateTimeFormat),
              }
            : '-',
          'Acknowledged?': acknowledgedCell,
          'Action Count': actionCountCell,
        }
      })

      const headers = Object.keys(body[0])

      return (
        <section className="text-base-content text-base h-full flex flex-col">
          <div className="min-h-0 flex-1">
            <SimpleTable headers={headers} body={body} />
          </div>
        </section>
      )
    },

    Caption: ({ configuration, status }) => {
      const { companies } = useCompany()
      const params = configuration.parameters as AlertAcknowledgementDetailParams

      if (status.code === InsightStatusCode.ERROR) {
        return ''
      }

      const fields: Record<string, string> = {}

      if (params.companyId) {
        const company = companies.find((c) => c.id === params.companyId)
        if (company) fields['Company'] = company.name
      }

      if (params.status) {
        const statusDisplay = statusOptions.find((o) => o.value === params.status)?.label
        if (statusDisplay) fields['Status'] = statusDisplay
      }

      if (params.severity?.length) {
        const severities = typeof params.severity === 'string' ? [params.severity] : params.severity
        const labels = severities
          .map((s) => getSeverityLabel(s))
          .filter(Boolean)
          .join(', ')
        fields['Severity'] = labels
      }

      addDateToCaptionFields({ ...params, fields, finite: true })

      if (Object.keys(fields).length) {
        return <Caption fields={fields} />
      }
      return ''
    },

    Footer: ({ data, status }) => {
      if (status.code === InsightStatusCode.LOADING && data === undefined) {
        return ''
      }
      if (status.code === InsightStatusCode.NO_DATA) {
        return <div>No records found</div>
      }
      const rows = data?.length
      if (!rows) {
        return <div>No records found</div>
      }
      return (
        <div>
          {rows} row{rows === 1 ? '' : 's'} found
        </div>
      )
    },
  }

export default AlertAcknowledgementDetail
